import { Row, Col, Form, Button } from "react-bootstrap"
import { useState } from "react";
import { connect, useDispatch } from "react-redux";
import { getRankinItems, resetItems } from "../../../../../../redux/actions/account";
import { IAppState } from "../../../../../../redux/storeTypes";
import { clearItemSearch, setItemSearch } from "../../../../../../redux/actions/orderManagement";

interface IRankinSearch {
    nameLabel: string,
    namePlaceholder: string,
    caseNumberLabel: string,
    caseNumberPlaceholder: string,
    dateOfBirthLabel: string,
    searchHelpText: string,
    searchLabel: string,
    resetLabel: string,
    margin: string,
    padding: string,
    resetButtonBackgroundColor: string,
    resetButtonFontColor: string,
    resetButtonFontSize: string,
    resetButtonFontStyle: string,
    resetButtonFontWeight: string,
    resetButtonBorderColor: string,
    submitButtonBackgroundColor: string,
    submitButtonFontColor: string,
    submitButtonFontSize: string,
    submitButtonFontStyle: string,
    submitButtonFontWeight: string,
    nameWildcard?: boolean,
    caseNumberWildcard?: boolean,
    isFetching?: boolean,
}

export const NAME_METADATA_KEY = 'DefendantName';
export const CASE_NUMBER_METADATA_KEY = 'CaseNumber';
export const DATE_OF_BIRTH_METADATA_KEY = 'BirthDate';

export const NAME_LABEL = 'Name';
export const NAME_PLACEHOLDER = 'Enter full name as it appears on your citation';
export const CASE_NUMBER_LABEL = 'Case Number';
export const CASE_NUMBER_PLACEHOLDER = 'Enter case number';
export const DATE_OF_BIRTH_LABEL = 'Date of Birth';
export const SEARCH_HELP_TEXT = 'Please enter at least 2 of the following: Name, Case Number, and Date of Birth. Enter the exact information as it appears on your citation - do not use abbreviations or nicknames.';
export const SEARCH_LABEL = 'Search';
export const RESET_LABEL = 'Reset';
export const MARGIN = '0px';
export const PADDING = '0px';
export const RESET_BUTTON_FONT_COLOR = '#414141';
export const RESET_BUTTON_FONT_SIZE = '.875rem';
export const RESET_BUTTON_FONT_STYLE = 'normal';
export const RESET_BUTTON_FONT_WEIGHT = 'normal';
export const RESET_BUTTON_BORDER_COLOR = '#D9D9D9';
export const RESET_BUTTON_BACKGROUND_COLOR = '#FFFFFF';
export const SUBMIT_BUTTON_FONT_COLOR = '#FFFFFF';
export const SUBMIT_BUTTON_FONT_SIZE = '.875rem';
export const SUBMIT_BUTTON_FONT_STYLE = 'normal';
export const SUBMIT_BUTTON_FONT_WEIGHT = 'normal';
export const SUBMIT_BUTTON_BACKGROUND_COLOR = '#0057b6';
export const NAME_WILDCARD = false;
export const CASE_NUMBER_WILDCARD = false;

const checkStyleForDefault = (style: string, defaultStyle: string) => {
    return style && style !== '' ? style : defaultStyle;
}

const checkStyleForDefault3 = (defaultStyle: string) => {
    return defaultStyle as any;
}

const RankinSearch = ({ nameLabel, namePlaceholder, caseNumberLabel, caseNumberPlaceholder, dateOfBirthLabel, searchHelpText, searchLabel, resetLabel,
    margin, padding, resetButtonBackgroundColor, resetButtonFontColor, resetButtonFontSize, resetButtonFontStyle, resetButtonFontWeight, resetButtonBorderColor,
    submitButtonBackgroundColor, submitButtonFontColor, submitButtonFontSize, submitButtonFontStyle, submitButtonFontWeight, nameWildcard, caseNumberWildcard, isFetching }: IRankinSearch) => {

    if (!nameLabel) { nameLabel = NAME_LABEL }
    if (!namePlaceholder) { namePlaceholder = NAME_PLACEHOLDER }
    if (!caseNumberLabel) { caseNumberLabel = CASE_NUMBER_LABEL }
    if (!caseNumberPlaceholder) { caseNumberPlaceholder = CASE_NUMBER_PLACEHOLDER }
    if (!dateOfBirthLabel) { dateOfBirthLabel = DATE_OF_BIRTH_LABEL }
    if (!searchHelpText) { searchHelpText = SEARCH_HELP_TEXT }
    if (!searchLabel) { searchLabel = SEARCH_LABEL }
    if (!resetLabel) { resetLabel = RESET_LABEL }
    if (!margin) { margin = MARGIN }
    if (!padding) { padding = PADDING }
    if (!resetButtonBackgroundColor) { resetButtonBackgroundColor = RESET_BUTTON_BACKGROUND_COLOR }
    if (!resetButtonFontColor) { resetButtonFontColor = RESET_BUTTON_FONT_COLOR }
    if (!resetButtonFontSize) { resetButtonFontSize = RESET_BUTTON_FONT_SIZE }
    if (!resetButtonFontStyle) { resetButtonFontStyle = RESET_BUTTON_FONT_STYLE }
    if (!resetButtonFontWeight) { resetButtonFontWeight = RESET_BUTTON_FONT_WEIGHT }
    if (!resetButtonBorderColor) { resetButtonBorderColor = RESET_BUTTON_BORDER_COLOR }
    if (!submitButtonBackgroundColor) { submitButtonBackgroundColor = SUBMIT_BUTTON_BACKGROUND_COLOR }
    if (!submitButtonFontColor) { submitButtonFontColor = SUBMIT_BUTTON_FONT_COLOR }
    if (!submitButtonFontSize) { submitButtonFontSize = SUBMIT_BUTTON_FONT_SIZE }
    if (!submitButtonFontStyle) { submitButtonFontStyle = SUBMIT_BUTTON_FONT_STYLE }
    if (!submitButtonFontWeight) { submitButtonFontWeight = SUBMIT_BUTTON_FONT_WEIGHT }

    const [name, setName] = useState<string>('');
    const [caseNumber, setCaseNumber] = useState<string>('');
    const [dateOfBirth, setDateOfBirth] = useState<string>('');
    const actionToken = 'RankinSearch';
    const dispatch = useDispatch();

    const populatedFieldCount = [name, caseNumber, dateOfBirth].filter(value => value?.trim().length > 0).length;
    const meetsMinimumFields = populatedFieldCount >= 2;

    const handleSearch = () => {
        const fields = [
            { key: NAME_METADATA_KEY, value: name.trim(), isWildCard: nameWildcard ?? NAME_WILDCARD },
            { key: CASE_NUMBER_METADATA_KEY, value: caseNumber.trim(), isWildCard: caseNumberWildcard ?? CASE_NUMBER_WILDCARD },
            { key: DATE_OF_BIRTH_METADATA_KEY, value: dateOfBirth.trim(), isWildCard: false },
        ];
        dispatch(setItemSearch({ searchBy: 'Rankin', fields }, 'ItemSearch'));
        dispatch(getRankinItems(fields, actionToken));
    }

    const handleReset = () => {
        dispatch(clearItemSearch());
        dispatch(resetItems());
        setName('');
        setCaseNumber('');
        setDateOfBirth('');
    }

    return (
        <div style={{ margin: margin, padding: padding }}>
            <Row>
                <Col>
                    <p>{searchHelpText}</p>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group controlId="name">
                        <Form.Label>{nameLabel}</Form.Label>
                        <Form.Control type="input" placeholder={namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group controlId="caseNumber">
                        <Form.Label>{caseNumberLabel}</Form.Label>
                        <Form.Control type="input" placeholder={caseNumberPlaceholder} value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group controlId="dateOfBirth">
                        <Form.Label>{dateOfBirthLabel}</Form.Label>
                        <Form.Control type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col>
                    <Button className="brand-btn-primary" style={{
                        marginTop: '29px',
                        backgroundColor: checkStyleForDefault(resetButtonBackgroundColor, RESET_BUTTON_BACKGROUND_COLOR),
                        borderColor: checkStyleForDefault(resetButtonBorderColor, RESET_BUTTON_BORDER_COLOR),
                        color: checkStyleForDefault(resetButtonFontColor, RESET_BUTTON_FONT_COLOR),
                        fontSize: checkStyleForDefault(resetButtonFontSize, RESET_BUTTON_FONT_SIZE),
                        fontStyle: checkStyleForDefault(resetButtonFontStyle, RESET_BUTTON_FONT_STYLE),
                        fontWeight: checkStyleForDefault3(resetButtonFontWeight)
                    }} onClick={handleReset} disabled={isFetching}>
                        {resetLabel}
                    </Button>
                    <Button className="brand-btn-primary" style={{
                        marginTop: '29px',
                        marginLeft: '8px',
                        backgroundColor: checkStyleForDefault(submitButtonBackgroundColor, SUBMIT_BUTTON_BACKGROUND_COLOR),
                        borderColor: checkStyleForDefault(submitButtonBackgroundColor, SUBMIT_BUTTON_BACKGROUND_COLOR),
                        color: checkStyleForDefault(submitButtonFontColor, SUBMIT_BUTTON_FONT_COLOR),
                        fontSize: checkStyleForDefault(submitButtonFontSize, SUBMIT_BUTTON_FONT_SIZE),
                        fontStyle: checkStyleForDefault(submitButtonFontStyle, SUBMIT_BUTTON_FONT_STYLE),
                        fontWeight: checkStyleForDefault3(submitButtonFontWeight)
                    }} disabled={!meetsMinimumFields || isFetching} onClick={handleSearch}>
                        {searchLabel}
                    </Button>
                </Col>
            </Row>
        </div>
    )
}

const mapStateToProps = (state: IAppState) => {
    return {
        isFetching: state.account.isFetching,
    };
};

export default connect(mapStateToProps)(RankinSearch);
